import { localizedDigits } from './arabicDigits';
import { getFormValueLabel } from './formValueLabels';
import { isArabicScript } from './scriptConventions';

/**
 * What to call the other person in a conversation.
 *
 * Deliberately thin. The DECISION — whether this member has agreed to be known
 * to this viewer — is made on the server, which sends `other_display_name`
 * only once a conversation exists and omits it otherwise. This does not
 * re-derive that rule; it prefers the name when the server chose to send one.
 *
 * That distinction matters: a second copy of "am I matched yet?" on a client
 * is a copy that drifts, which is what INV-MATCH-16 was written for. The
 * client's only job is to render what it was given.
 *
 * Most members choose no name, so this returns the profile code — exactly what
 * every screen showed before.
 *
 * Never the registration name: `users.name` is not sent to other members at
 * all, so it cannot appear here.
 */
export function memberLabel(conversation, fallback = '') {
  const chosen = conversation?.other_display_name;
  if (typeof chosen === 'string' && chosen.trim()) return chosen.trim();

  const code = conversation?.other_form_code;
  if (typeof code === 'string' && code.trim()) return code.trim();

  return fallback;
}

/**
 * The name a PROFILE's owner chose, when the server sent it. Same contract as
 * above: the server decides who may see it (everyone once its owner was told
 * it is public, otherwise only someone in a conversation with her —
 * backend services/member_label.py). Null means "show the code / age line".
 */
export function profileName(form) {
  const chosen = form?.display_name;
  return typeof chosen === 'string' && chosen.trim() ? chosen.trim() : null;
}

// Arabic script takes its own comma: beside Arabic-Indic digits a middle dot
// IS the Arabic zero (٠) - «فتاة · ٢٦ · القاهرة» read as «٠٢٦٠» on a screen.
const SEPARATOR = ' · ';
const ARABIC_SCRIPT_SEPARATOR = '، ';
const usablePlace = (value) => {
  const text = typeof value === 'string' ? value.trim() : '';
  return text === 'أخرى' ? '' : text;
};

/**
 * Who a member is, for a screen with no name to show: «فتاة، ٢٨، القاهرة».
 *
 * A profile code is a support reference and reads like a ticket number, so it
 * is never a member's title (owner decision 2026-11-02). Mirrors the server's
 * `services/member_label.py::member_description` (chat list, chat header,
 * notifications) and mobile `member_description.dart`, so one person reads the
 * same everywhere: the CITY in Arabic, the COUNTRY in every other language -
 * the server has no city names to match. `includeSide` is false where the row
 * already shows the side. '' when the form says nothing usable.
 */
export function describeMember(form, t, language, { includeSide = true } = {}) {
  if (!form) return '';
  const side = form.form_type;
  const parts = [];
  if (includeSide && (side === 'bride' || side === 'groom')) {
    parts.push(t(`memberLabel.${side}`));
  }
  const specs = form[`${side}_specs`] || form.bride_specs || form.groom_specs || {};
  const age = Number(specs.age);
  if (Number.isFinite(age) && age >= 18 && age <= 99) {
    parts.push(localizedDigits(Math.trunc(age), language));
  }
  const residence = form.residence_info || {};
  const city = usablePlace(residence.governorate) || usablePlace(residence.city);
  let country = usablePlace(residence.country_of_residence);
  // A null country from an old client means Egypt (CLAUDE.md, Forms).
  if (!country && city) country = 'مصر';
  const isArabic = String(language || 'ar').toLowerCase().startsWith('ar');
  const place = isArabic ? (city || country) : (country ? getFormValueLabel(country, t) : '');
  if (place) parts.push(place);
  return parts.join(isArabicScript(language) ? ARABIC_SCRIPT_SEPARATOR : SEPARATOR);
}

/**
 * A row's title when its own subtitle already says age and place: the
 * public name, else just the side («فتاة» / «شاب»). Never the code.
 */
export function nameOrSide(form, t) {
  return profileName(form) || (form?.form_type === 'bride' ? t('memberLabel.bride') : t('memberLabel.groom'));
}

export default memberLabel;
